import {
  Box, Divider, Drawer, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import HomeIcon from '@mui/icons-material/Home';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PersonIcon from '@mui/icons-material/Person';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getAppVersion } from "../../utils/MetaService";
import { AppVersion } from "../../models/AppVersion";

export type MenuProps = {
  open: boolean;
  onClose: () => void;
}

const navItems = [
  { key: 'home', icon: <HomeIcon />, path: '/', labelKey: 'home' },
  { key: 'new_recipe', icon: <AddCircleOutlineIcon />, path: '/recipe/edit/-1', labelKey: 'newRecipe' },
  { key: 'import_recipe', icon: <ContentPasteGoIcon />, path: '/import', labelKey: 'importRecipe' },
  { key: 'recipe_plan', icon: <CalendarMonthIcon />, path: '/recipePlan', labelKey: 'recipePlaner' },
  { key: 'shopping_list', icon: <ShoppingCartIcon />, path: '/shoppingList', labelKey: 'shoppingList' },
  { key: 'match', icon: <FavoriteIcon />, path: '/match', labelKey: 'match' },
];

const Menu = (props: MenuProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [appVersion, setAppVersion] = useState<AppVersion | null>(null);

  useEffect(() => { getAppVersion().then(setAppVersion); }, []);

  const changeRoute = (path: string) => {
    navigate(path);
    props.onClose();
  };

  return (
    <Drawer
      open={props.open}
      onClose={props.onClose}
      PaperProps={{
        sx: {
          width: 280,
          borderRadius: '0 20px 20px 0',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Brand header */}
      <Box
        sx={{
          px: 3,
          py: 3,
          background: 'linear-gradient(135deg, #D97706 0%, #92400E 100%)',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <MenuBookIcon sx={{ color: 'rgba(255,255,255,0.95)', fontSize: 30 }} />
        <Box>
          <Typography variant="h6" fontWeight={800} sx={{ color: '#fff', lineHeight: 1.1 }}>
            Cookbook
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>
            Your recipe companion
          </Typography>
        </Box>
      </Box>

      {/* Main nav */}
      <List sx={{ px: 1, pt: 1.5, flex: 1 }}>
        {navItems.map((item) => (
          <ListItem key={item.key} disablePadding>
            <ListItemButton onClick={() => changeRoute(item.path)}>
              <ListItemIcon sx={{ color: 'primary.main' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={t(item.labelKey)}
                primaryTypographyProps={{ fontWeight: 500, fontSize: '0.95rem' }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ mx: 2 }} />

      {/* Account at bottom */}
      <List sx={{ px: 1, pb: 2, pt: 0.5 }}>
        <ListItem disablePadding>
          <ListItemButton onClick={() => changeRoute('/account')}>
            <ListItemIcon sx={{ color: 'text.secondary' }}>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText
              primary={t('account')}
              primaryTypographyProps={{ fontWeight: 500, fontSize: '0.95rem' }}
            />
          </ListItemButton>
        </ListItem>
      </List>

      {appVersion && (
        <Typography
          variant="caption"
          align="center"
          sx={{ display: 'block', color: 'text.disabled', pb: 1.5 }}
        >
          v{appVersion.version}
        </Typography>
      )}
    </Drawer>
  );
};

export default Menu;
